import React, { useState, useEffect, useRef, useContext } from "react";
import CenterContainer from "./CenterContainer";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faList, faIdCard, faSignInAlt, faCompass, faBars, faUserNinja, faQuestion, faCommentDots } from "@fortawesome/free-solid-svg-icons";
import { Link, NavLink } from "react-router-dom";
import ReactDOM from "react-dom";
import AuthContext from "../AuthContext";
import { useLanguage } from "../useLanguage";
import { StatusContext } from "../StatusContext";
import isDevelopment from "consumerhouse-shared";
import { LanguageContext } from "../LanguageContext";

const Filler = styled.div`
    height: 68px;
`;

const Icon = styled.div`
    top: 5px;
    left: 0;
    position: absolute;
    background: url("/house96.png");
    height: 50px;
    width: 50px;
    background-size: contain;
`;

const TitleContainer = styled(Link)`
    text-decoration: none;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    margin: 0.5em 0 0.5em 60px;
    align-self: center;
    line-height: 0.9;
    color: white;
`;

const Title = styled.h2`
    margin: 0.2em 0 0 0;
    display: inline-block;
`;
const Subtitle = styled.span`
    opacity: 0.8;
    font-size: 0.9em;
`;

const NavContainer = styled.nav`
    position: relative;
    display: flex;
    align-items: flex-end;
    height: 60px;
`;

const NavItemIcon = styled(FontAwesomeIcon)`
    font-size: 1.1em;
    margin: 0 0.5em 0 0;

    @media only screen and (max-width: 900px) {
        font-size: 1.3em;
    }
`;

const MenuButton = styled.span`
    position: absolute;
    right: 0;
    top: 0;
    padding: 0.18em;
    font-size: 2.5em;
    color: white;

    @media only screen and (min-width: 900px) {
        display: none;
    }
`;

const NavItemContainer = styled.div`
    display: flex;
    align-items: flex-end;
    transform-origin: top right;
    overflow: hidden;

    @media only screen and (max-width: 900px) {
        flex-direction: column;
        align-items: stretch;
        background: #eee;
        border-radius: 1em;
        position: absolute;
        right: 0;
        top: 4em;
        box-shadow: -2px 2px 15px #0004;
        background: #d3545f;

        display: block;
        transition: 100ms;
        transform: scale(0);

        &.mobile-shown {
            transition: 100ms;
            transform: scale(1);
        }
    }
`;

const LanguageContainer = styled.div`
    display: flex;
    border-radius: 0.5em 0.5em 0 0;
    background: #0002;
    margin-left: 0.5em;

    @media only screen and (max-width: 900px) {
        display: none;
    }
`;

const LanguageItem = styled.a`
    padding: 0.5em;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    img {
        width: 27px;
    }
    &:hover {
        transition: 100ms;
        transform: scale(1.1);
    }
`;

const NavItem = styled(NavLink)`
    background: #0003;
    padding: 0.5em 1.3em 0.4em 1.3em;
    margin: 0em 0.2em;
    font-size: 1.05em;
    color: white;
    font-weight: bold;
    border-bottom: 3px solid white;
    border-radius: 0.5em 0.5em 0 0;
    cursor: pointer;
    text-decoration: none;
    transform-origin: bottom center;
    transition: 100ms;

    &:hover,
    &.active {
        transition: 100ms;
        background: #0003;
        border-bottom: 0px solid white;
    }

    &:active {
        transform: rotateX(25deg);
    }

    @media only screen and (max-width: 900px) {
        padding: 1em;
        display: flex;
        align-items: center;
        background: #d3545f;
        border: none;
        border-radius: 0;
        cursor: pointer;
        font-size: 1.2em;
        margin: 0;
    }
`;

const Center = styled(CenterContainer)`
    border-top: 0.5em solid #3a3a3c; //#bf1e2d;
    background: linear-gradient(90deg, #eb3c48, #dc354f); // linear-gradient(45deg, #ec454f, #dc354f)
    position: fixed;
    top: 0;
    left: 0;
    box-shadow: 0 5px 10px #0005;
    z-index: 50;
`;

export default function NavBar() {
    const [menu, setMenu] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const authState = useContext(AuthContext);
    const { language: languageName } = useContext(LanguageContext);
    const language = useLanguage();
    const status = useContext(StatusContext);

    useEffect(() => {
        function handleClick(ev: MouseEvent) {
            if (menu && ev.target instanceof HTMLElement && !menuRef.current?.contains(ev.target)) {
                setMenu(false);
            }
        }
        window.addEventListener("mousedown", handleClick);
        return () => window.removeEventListener("mousedown", handleClick);
    }, []);

    return (
        <>
            <Center>
                <NavContainer ref={menuRef}>
                    <Icon />
                    <TitleContainer to="/">
                        <Title>
                            {isDevelopment && "Dev "}
                            {language("nav-main")}
                        </Title>
                        <Subtitle>ConsumerHouse</Subtitle>
                    </TitleContainer>

                    <MenuButton onClick={() => setMenu(!menu)}>
                        <FontAwesomeIcon icon={faBars} />
                    </MenuButton>
                    <NavItemContainer className={menu ? "mobile-shown" : ""}>
                        {authState.auth && (
                            <>
                                {!status?.maintenanceMode && (
                                    <>
                                        <NavItem
                                            exact
                                            to="/"
                                            onClick={() => {
                                                setMenu(false);
                                            }}>
                                            <NavItemIcon icon={faList} /> {language("nav-menu-tests")}
                                        </NavItem>
                                        {/* <NavItem
                                            exact
                                            to="/faq"
                                            onClick={() => {
                                                setMenu(false);
                                            }}>
                                            <NavItemIcon icon={faCommentDots} /> {language("faq")}
                                        </NavItem> */}
                                        <NavItem
                                            exact
                                            to="/account"
                                            onClick={() => {
                                                setMenu(false);
                                            }}>
                                            <NavItemIcon icon={faIdCard} /> {language("nav-menu-my-account")}
                                        </NavItem>
                                    </>
                                )}
                                {authState.auth && authState.auth.user.admin && (
                                    <NavItem
                                        to="/admin"
                                        onClick={() => {
                                            setMenu(false);
                                        }}>
                                        <FontAwesomeIcon icon={faUserNinja} />
                                    </NavItem>
                                )}
                            </>
                        )}

                        {!authState.auth && (
                            <>
                                {/* <NavItem
                                    exact
                                    to="/faq"
                                    onClick={() => {
                                        setMenu(false);
                                    }}>
                                    <NavItemIcon icon={faCommentDots} /> {language("faq")}
                                </NavItem> */}
                                {!status?.maintenanceMode && (
                                    <>
                                        <NavItem
                                            exact
                                            to="/account"
                                            onClick={() => {
                                                setMenu(false);
                                            }}>
                                            <NavItemIcon icon={faSignInAlt} /> {language("nav-menu-login")}
                                        </NavItem>
                                    </>
                                )}
                            </>
                        )}
                    </NavItemContainer>

                    {languageName !== "nl_nl" && (
                        <LanguageContainer>
                            {languageName !== "fr" && (
                                <LanguageItem href="https://etudes-consumerhouse.be/" title="Utiliser le français">
                                    <img src="/fr.png" alt="FR" />
                                </LanguageItem>
                            )}
                            {languageName !== "nl" && (
                                <LanguageItem href="https://onderzoeken-consumerhouse.be/" title="Gebruik nederlands">
                                    <img src="/nl.png" alt="NL" />
                                </LanguageItem>
                            )}
                        </LanguageContainer>
                    )}
                </NavContainer>
            </Center>
            <Filler />
        </>
    );
}
